import { useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  CheckCircle2,
  Eye,
  EyeOff,
  LockKeyhole,
  ShieldCheck,
  UserRound,
} from 'lucide-react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import Alert from '../components/common/Alert';
import BrandMark from '../components/common/BrandMark';
import Button from '../components/common/Button';
import Input from '../components/forms/Input';
import { useAuth } from '../context/AuthContext';

function LoginPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [authError, setAuthError] = useState('');
  const { isAuthenticated, isLoading, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: { username: '', password: '' },
  });

  if (isLoading) return null;
  if (isAuthenticated) {
    return <Navigate to="/"
      replace />;
  }

  const onSubmit = async ({ username, password }) => {
    setAuthError('');
    try {
      const authenticatedUser = await login(username, password);
      navigate(
        authenticatedUser.must_change_password
          ? '/cambiar-contrasena'
          : authenticatedUser.role === 'VOTANTE'
            ? '/mi-perfil'
          : location.state?.from?.pathname || '/dashboard',
        { replace: true }
      );
    } catch (error) {
      setAuthError(error.message);
    }
  };

  return (
    <div className="min-h-screen bg-primary-950 lg:grid lg:grid-cols-[1.1fr_0.9fr]">
      <section className="relative hidden overflow-hidden border-r border-white/10 p-12 lg:flex lg:flex-col lg:justify-between">
        <div className="pointer-events-none absolute -left-32 top-1/3 size-[32rem] rounded-full bg-primary-500/10 blur-3xl" />
        <BrandMark />
        <div className="relative max-w-xl">
          <span className="inline-flex items-center gap-2 rounded-full border border-primary-400/20 bg-primary-400/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-primary-300">
            <ShieldCheck className="size-3.5" />
            Gestión segura por roles
          </span>
          <h1 className="mt-6 text-5xl font-semibold leading-tight tracking-tight text-white">
            Una estructura organizada para una campaña mejor coordinada.
          </h1>
          <p className="mt-5 max-w-lg text-lg leading-8 text-slate-400">
            Centraliza responsables, votantes y operaciones con accesos adecuados para cada integrante del equipo.
          </p>
          <div className="mt-8 space-y-3 text-sm text-slate-300">
            {['Accesos diferenciados por rol', 'Información centralizada en PostgreSQL', 'Autenticación segura mediante JWT'].map((item) => (
              <div key={item} className="flex items-center gap-3">
                <CheckCircle2 className="size-4 text-primary-300" />
                {item}
              </div>
            ))}
          </div>
        </div>
        <p className="text-xs text-slate-600">Votum 2.0 · Gestión electoral</p>
      </section>

      <main className="flex min-h-screen items-center justify-center bg-surface-page px-5 py-10 sm:px-8">
        <div className="w-full max-w-md">
          <div className="mb-8 lg:hidden">
            <div className="inline-flex rounded-xl bg-primary-950 p-3">
              <BrandMark />
            </div>
          </div>
          <div>
            <p className="text-sm font-semibold text-primary-700">Bienvenido de nuevo</p>
            <h2 className="mt-2 text-3xl font-semibold tracking-tight text-content-primary">Iniciar sesión</h2>
            <p className="mt-2 text-sm leading-6 text-content-muted">Ingresa con tu número de cédula para acceder al panel administrativo.</p>
          </div>

          {authError && (
            <Alert variant="danger" title="No pudimos iniciar sesión" className="mt-6">
              {authError}
            </Alert>
          )}

          <form className="mt-7 space-y-5" onSubmit={handleSubmit(onSubmit)} noValidate>
            <Input
              id="username"
              label="Número de cédula"
              icon={UserRound}
              placeholder="Ingresa tu número de cédula"
              autoComplete="username"
              required
              inputMode="numeric"
              error={errors.username?.message}
              {...register('username', {
                required: 'Ingresa tu número de cédula.',
                minLength: { value: 3, message: 'Debe contener al menos 3 caracteres.' },
              })}
            />
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              label="Contraseña"
              icon={LockKeyhole}
              placeholder="Ingresa tu contraseña"
              autoComplete="current-password"
              required
              error={errors.password?.message}
              endAdornment={
                <button
                  type="button"
                  onClick={() => setShowPassword((value) => !value)}
                  className="grid size-8 place-items-center rounded-md text-content-muted transition hover:bg-slate-100 hover:text-content-primary"
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                </button>
              }
              {...register('password', {
                required: 'Ingresa tu contraseña.',
              })}
            />
            <Button type="submit" size="lg" className="w-full" loading={isSubmitting}>
              Iniciar sesión
            </Button>
          </form>

        </div>
      </main>
    </div>
  );
}

export default LoginPage;
